import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Mail, ShieldCheck, Send, Info } from "lucide-react";
import { toast } from "sonner";

export default function SaaSEmail() {
  const [cfg, setCfg] = useState({
    enabled: false, host: "smtp.gmail.com", port: 587,
    username: "", password: "", sender_email: "", sender_name: "CareO Billing",
  });
  const [info, setInfo] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/settings/saas-smtp");
      setInfo(data);
      setCfg(c => ({
        ...c,
        enabled: data.enabled || false,
        host: data.host || "smtp.gmail.com",
        port: data.port || 587,
        username: data.username || "",
        password: "",  // never prefill secret; blank keeps existing
        sender_email: data.sender_email || "",
        sender_name: data.sender_name || "CareO Billing",
      }));
      if (!testEmail) setTestEmail(data.username || "");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const set = (k, v) => setCfg(s => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/settings/saas-smtp", cfg);
      toast.success("SaaS email settings saved");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testEmail) { toast.error("Enter a recipient email"); return; }
    setTesting(true);
    try {
      const { data } = await api.post("/settings/saas-smtp/test", { email: testEmail });
      if (data.ok && !data.stub) toast.success(`Test email sent to ${testEmail}`);
      else if (data.stub) toast.message("SMTP disabled — saved in stub mode");
      else toast.error(data.message || "Test failed");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl" data-testid="saas-email-page">
      <div>
        <div className="section-label">SaaS Console</div>
        <h1 className="font-heading text-3xl font-semibold">SaaS email (Gmail SMTP)</h1>
        <p className="mt-1 text-sm text-ink-muted">Used by the SaaS owner to send payment receipts, renewal reminders, and password-reset emails to hospitals.</p>
      </div>

      <Card className="border-sky-200 bg-sky-50/40 p-4">
        <div className="flex items-start gap-2">
          <Info size={16} className="mt-0.5 text-sky-700" />
          <div className="text-sm text-sky-900">
            <p className="font-medium">Gmail App Password setup</p>
            <ol className="mt-1 list-decimal pl-5 space-y-1">
              <li>Enable 2-Step Verification on your Google account.</li>
              <li>Visit <span className="font-mono">myaccount.google.com/apppasswords</span> and create an app password named "CareO".</li>
              <li>Paste the 16-character app password below (no spaces).</li>
            </ol>
            <p className="mt-1 italic">Tenant-level SMTP (per-hospital) is separate — that's in Admin → Settings within each hospital.</p>
          </div>
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <div className="flex items-center gap-3">
          <Switch checked={cfg.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="saas-smtp-toggle" />
          <div>
            <Label className="text-base">Enable SaaS Gmail SMTP</Label>
            <p className="text-xs text-ink-muted">When OFF, outgoing SaaS emails are logged but not sent.</p>
          </div>
          {info.password_set && <span className="ml-auto text-xs text-emerald-700 font-medium flex items-center gap-1"><ShieldCheck size={12} /> App password configured</span>}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label>SMTP host</Label>
            <Input value={cfg.host} onChange={e => set("host", e.target.value)} placeholder="smtp.gmail.com" data-testid="saas-smtp-host" />
          </div>
          <div>
            <Label>Port</Label>
            <Input type="number" value={cfg.port} onChange={e => set("port", Number(e.target.value))} placeholder="587 (TLS) or 465 (SSL)" data-testid="saas-smtp-port" />
          </div>
          <div>
            <Label>Gmail address (username)</Label>
            <Input value={cfg.username} onChange={e => set("username", e.target.value)} placeholder="info@stewardindia.com" data-testid="saas-smtp-username" />
          </div>
          <div>
            <Label>App password {info.password_set && <span className="text-xs text-ink-muted ml-1">(current: {info.password})</span>}</Label>
            <Input type="password" value={cfg.password} onChange={e => set("password", e.target.value)} placeholder={info.password_set ? "Leave blank to keep current" : "16-char app password"} data-testid="saas-smtp-password" />
          </div>
          <div>
            <Label>Sender email (From)</Label>
            <Input value={cfg.sender_email} onChange={e => set("sender_email", e.target.value)} placeholder="defaults to username" data-testid="saas-smtp-sender-email" />
          </div>
          <div>
            <Label>Sender name (display)</Label>
            <Input value={cfg.sender_name} onChange={e => set("sender_name", e.target.value)} placeholder="CareO Billing" data-testid="saas-smtp-sender-name" />
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-2">
          <Mail className="text-brand" size={18} />
          <h3 className="font-heading text-lg font-semibold">Send test email</h3>
        </div>
        <p className="mt-1 text-sm text-ink-muted">Verify your configuration by sending a sample message.</p>
        <div className="mt-3 flex gap-2">
          <Input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="recipient@example.com" data-testid="saas-smtp-test-email" />
          <Button onClick={sendTest} disabled={testing || !cfg.enabled} variant="outline" data-testid="saas-smtp-test-btn">
            <Send size={14} className="mr-2" />{testing ? "Sending…" : "Send test"}
          </Button>
        </div>
        {!cfg.enabled && <p className="mt-2 text-xs text-amber-700">Enable the toggle above first; then save before testing.</p>}
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-700" data-testid="saas-smtp-save-btn">
          {saving ? "Saving…" : "Save settings"}
        </Button>
      </div>
    </div>
  );
}
